import { useMutation, useQuery, UseQueryResult } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import qs from 'qs';
import { useAxiosClientsContextProvider } from 'shared/axios/axios-clients-provider';

import { BulkUpdateOrganisationUnitScenarioIsActiveCommand } from 'features/residents/models/bulk-update-organisation-unit-scenario-is-active';
import CreateTransmitterCommand from 'features/residents/models/create-transmitter-command';
import CreateTransmitterResponse from 'features/residents/models/create-transmitter-response';
import EditResidentCommand from 'features/residents/models/edit-resident-command';
import { serialize } from 'features/residents/models/organisation-unit-scenario';
import ReadBulkAvailableScenariosQuery from 'features/residents/models/read-bulk-available-scenarios-query';
import ReadBulkAvailableScenariosResponse from 'features/residents/models/read-bulk-available-scenarios-response';
import ReadLinkableWanderingBoundariesQuery from 'features/residents/models/read-linkable-wandering-boundaries-query';
import ReadLinkableWanderingBoundariesResponse from 'features/residents/models/read-linkable-wandering-boundaries-response';
import ReadResidentsFiltersQuery from 'features/residents/models/read-residents-filters-query';
import ReadResidentFiltersResponse from 'features/residents/models/read-residents-filters-response';
import ReadResidentsQuery from 'features/residents/models/read-residents-query';
import ReadResidentsResponse from 'features/residents/models/read-residents-response';
import Resident from 'features/residents/models/resident';
import ValidateTransmitterCommand from 'features/residents/models/validate-transmitter-command';
import ValidateTransmitterResponse from 'features/residents/models/validate-transmitter-response';
import { WanderingBoundary } from 'features/residents/models/wandering-boundary';

const residentBaseUrl = '/api/v1/resident';
const residentBaseUrlV2 = '/api/v2/resident';
const residentBaseUrlV3 = '/api/v3/resident';

enum ResidentQueryPaths {
  ReadResidents = 'ReadResidents',
  EditResident = 'EditResident',
  ReadBulkAvailableScenarios = 'ReadBulkAvailableScenarios',
  BulkUpdateOrganisationUnitScenarioIsActive = 'BulkUpdateOrganisationUnitScenarioIsActive',
  ReadLinkableWanderingBoundaries = 'ReadLinkableWanderingBoundaries',
  ReadResidentSettingFilters = 'ReadResidentSettingFilters',
  ReadWanderingBoundariesForResident = 'ReadWanderingBoundariesForResident',
  ValidateTransmitter = 'ValidateTransmitter',
  CreateTransmitter = 'CreateTransmitter',
}

const readResidents = async (
  axiosInstance: AxiosInstance,
  readResidentsQuery: ReadResidentsQuery,
  signal?: AbortSignal,
): Promise<ReadResidentsResponse> => {
  const body = JSON.stringify(readResidentsQuery);
  const response = await axiosInstance.post(`${residentBaseUrlV3}/${ResidentQueryPaths.ReadResidents}`, body, { signal });
  return response.data;
};

const editResident = async (axiosInstance: AxiosInstance, editResidentCommand: EditResidentCommand): Promise<Resident> => {
  editResidentCommand.editOrganisationUnitScenarios = editResidentCommand.editOrganisationUnitScenarios.map((organisationUnitScenario) =>
    serialize(organisationUnitScenario),
  );
  const body = JSON.stringify(editResidentCommand);
  const response = await axiosInstance.put(`${residentBaseUrlV2}/${ResidentQueryPaths.EditResident}`, body);
  return response.data;
};

const readBulkAvailableScenarios = async (
  axiosInstance: AxiosInstance,
  readBulkAvailableScenariosQuery: ReadBulkAvailableScenariosQuery,
  signal?: AbortSignal,
): Promise<ReadBulkAvailableScenariosResponse> => {
  const body = JSON.stringify(readBulkAvailableScenariosQuery);
  const response = await axiosInstance.post(`${residentBaseUrl}/${ResidentQueryPaths.ReadBulkAvailableScenarios}`, body, { signal });
  return response.data;
};

const bulkUpdateOrganisationUnitScenarioIsActive = async (
  axiosInstance: AxiosInstance,
  bulkUpdateOrganisationUnitScenarioIsActiveCommand: BulkUpdateOrganisationUnitScenarioIsActiveCommand,
): Promise<BulkUpdateOrganisationUnitScenarioIsActiveCommand> => {
  const body = JSON.stringify(bulkUpdateOrganisationUnitScenarioIsActiveCommand);
  const response = await axiosInstance.put(`${residentBaseUrl}/${ResidentQueryPaths.BulkUpdateOrganisationUnitScenarioIsActive}`, body);
  return response.data;
};

const readLinkableWanderingBoundaries = async (
  axiosInstance: AxiosInstance,
  query: ReadLinkableWanderingBoundariesQuery,
  signal?: AbortSignal,
): Promise<ReadLinkableWanderingBoundariesResponse> => {
  const queryString = qs.stringify(query, { allowDots: true });
  const response = await axiosInstance.get(`${residentBaseUrl}/${ResidentQueryPaths.ReadLinkableWanderingBoundaries}?${queryString}`, {
    signal,
  });
  return response.data;
};

const readResidentSettingFilters = async (
  axiosInstance: AxiosInstance,
  readResidentsFiltersQuery: ReadResidentsFiltersQuery,
  signal?: AbortSignal,
): Promise<ReadResidentFiltersResponse> => {
  const body = JSON.stringify(readResidentsFiltersQuery);
  const response = await axiosInstance.post(`${residentBaseUrl}/${ResidentQueryPaths.ReadResidentSettingFilters}`, body, { signal });
  return response.data;
};

const readWanderingBoundariesForResident = async (
  axiosInstance: AxiosInstance,
  organisationUnitId: string,
  signal?: AbortSignal,
): Promise<WanderingBoundary[]> => {
  const response = await axiosInstance.get(
    `${residentBaseUrl}/${ResidentQueryPaths.ReadWanderingBoundariesForResident}/${organisationUnitId}`,
    {
      signal,
    },
  );
  return response.data;
};

export const useLazyReadResidents = (readResidentsQuery: ReadResidentsQuery) => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: [ResidentQueryPaths.ReadResidents, readResidentsQuery],
    queryFn: ({ signal }) => readResidents(authenticatedInstance, readResidentsQuery, signal),
    enabled: false,
  });
};

export const useEditResident = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useMutation({
    mutationKey: [ResidentQueryPaths.EditResident],
    mutationFn: (editResidentCommand: EditResidentCommand) => editResident(authenticatedInstance, editResidentCommand),
  });
};

export const useLazyReadBulkAvailableScenarios = (readBulkAvailableScenariosQuery: ReadBulkAvailableScenariosQuery) => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: [ResidentQueryPaths.ReadBulkAvailableScenarios],
    queryFn: ({ signal }) => readBulkAvailableScenarios(authenticatedInstance, readBulkAvailableScenariosQuery, signal),
    enabled: false,
  });
};

export const useUpdateBulkScenarioIsActive = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useMutation({
    mutationKey: [ResidentQueryPaths.BulkUpdateOrganisationUnitScenarioIsActive],
    mutationFn: (bulkUpdateOrganisationUnitScenarioIsActiveCommand: BulkUpdateOrganisationUnitScenarioIsActiveCommand) =>
      bulkUpdateOrganisationUnitScenarioIsActive(authenticatedInstance, bulkUpdateOrganisationUnitScenarioIsActiveCommand),
  });
};

export const useLazyReadLinkableWanderingBoundaries = (query: ReadLinkableWanderingBoundariesQuery) => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: [ResidentQueryPaths.ReadLinkableWanderingBoundaries, query],
    queryFn: ({ signal }) => readLinkableWanderingBoundaries(authenticatedInstance, query, signal),
    enabled: false,
  });
};

export const useLazyReadResidentFilters = (readResidentsFiltersQuery: ReadResidentsFiltersQuery) => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: [ResidentQueryPaths.ReadResidentSettingFilters],
    queryFn: ({ signal }) => readResidentSettingFilters(authenticatedInstance, readResidentsFiltersQuery, signal),
    enabled: false,
  });
};

export const useReadWanderingBoundariesForResident = (organisationUnitId: string): UseQueryResult<WanderingBoundary[]> => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: [ResidentQueryPaths.ReadWanderingBoundariesForResident, organisationUnitId],
    queryFn: ({ signal }) => readWanderingBoundariesForResident(authenticatedInstance, organisationUnitId, signal),
    enabled: !!organisationUnitId,
  });
};

const validateTransmitter = async (
  axiosInstance: AxiosInstance,
  command: ValidateTransmitterCommand,
): Promise<ValidateTransmitterResponse> => {
  const queryString = qs.stringify(command, { allowDots: true });
  const response = await axiosInstance.get(`${residentBaseUrl}/${ResidentQueryPaths.ValidateTransmitter}?${queryString}`);
  return response.data;
};

export const useValidateTransmitter = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useMutation({
    mutationKey: [ResidentQueryPaths.ValidateTransmitter],
    mutationFn: (command: ValidateTransmitterCommand) => validateTransmitter(authenticatedInstance, command),
  });
};

const createTransmitter = async (axiosInstance: AxiosInstance, command: CreateTransmitterCommand): Promise<CreateTransmitterResponse> => {
  const body = JSON.stringify(command);
  const response = await axiosInstance.post(`${residentBaseUrl}/${ResidentQueryPaths.CreateTransmitter}`, body);
  return response.data;
};

export const useCreateTransmitter = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useMutation({
    mutationKey: [ResidentQueryPaths.CreateTransmitter],
    mutationFn: (command: CreateTransmitterCommand) => createTransmitter(authenticatedInstance, command),
  });
};
